import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Stack,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import { adminAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import PortalLayout from '../../components/PortalLayout';

const STATUS_MAP: Record<string, { label: string; color: 'warning' | 'info' | 'success' | 'default' }> = {
  open: { label: 'Offen', color: 'warning' },
  called: { label: 'Angerufen', color: 'info' },
  closed: { label: 'Erledigt', color: 'success' },
};

const AdminCallbacksPage: React.FC = () => {
  const [callbacks, setCallbacks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const { showSuccess, showError } = useToast();

  const loadCallbacks = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {};
      if (filterStatus) params.status = filterStatus;
      const response = await adminAPI.getCallbacks(params);
      setCallbacks(response.data.data);
    } catch {
      showError('Fehler beim Laden der Rückrufanfragen');
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, showError]);

  useEffect(() => {
    loadCallbacks();
  }, [loadCallbacks]);

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await adminAPI.updateCallbackStatus(id, status);
      showSuccess('Status aktualisiert');
      loadCallbacks();
    } catch {
      showError('Fehler beim Aktualisieren des Status');
    }
  };

  return (
    <PortalLayout>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Rückrufanfragen
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            label="Status"
          >
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="open">Offen</MenuItem>
            <MenuItem value="called">Angerufen</MenuItem>
            <MenuItem value="closed">Erledigt</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : callbacks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">Keine Rückrufanfragen vorhanden.</Typography>
        </Paper>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Wunschzeit</TableCell>
              <TableCell>Nachricht</TableCell>
              <TableCell>Eingegangen</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {callbacks.map((cb) => (
              <TableRow key={cb.id}>
                <TableCell sx={{ fontWeight: 600 }}>{cb.name}</TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <PhoneIcon sx={{ fontSize: 16, color: '#667eea' }} />
                    <a href={`tel:${cb.phone}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                      {cb.phone}
                    </a>
                  </Stack>
                </TableCell>
                <TableCell>{cb.preferred_time || '–'}</TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography variant="body2" noWrap title={cb.message}>
                    {cb.message || '–'}
                  </Typography>
                </TableCell>
                <TableCell>{new Date(cb.created_at).toLocaleString('de-DE')}</TableCell>
                <TableCell>
                  <Chip
                    label={STATUS_MAP[cb.status]?.label || cb.status}
                    color={STATUS_MAP[cb.status]?.color || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5}>
                    {cb.status !== 'called' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        onClick={() => handleStatusChange(cb.id, 'called')}
                      >
                        Angerufen
                      </Button>
                    )}
                    {cb.status !== 'closed' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => handleStatusChange(cb.id, 'closed')}
                      >
                        Erledigt
                      </Button>
                    )}
                    {cb.status !== 'open' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => handleStatusChange(cb.id, 'open')}
                      >
                        Wieder öffnen
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </PortalLayout>
  );
};

export default AdminCallbacksPage;
